import { Check, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { AvailabilityPill } from '@/components/nfl/PlayerAvailability';
import { getDraftBoardPosColor } from '@/lib/nflDraftBoardColors';

/**
 * Prompt 2: the center column's players view.
 *
 * ── TRASH PROJECTION IS SORTED DESCENDING, DELIBERATELY ─────────────────────
 *
 * This is inverted scoring. `proj_sab_pts` HIGHER IS BETTER, so the best available player is
 * the one with the largest projection, and the top row -- the one the design highlights with
 * an orange wash -- is the max, not the min. Sorting this like a normal fantasy board would
 * silently invert the entire draft board's recommendation. SAB-458 already fixed exactly
 * this bug once on the server side (the waterfall used to order by adp ASC); the same trap
 * exists here on the client.
 *
 * ── DRAFTED PLAYERS STAY IN THE POOL (SAB-843) ──────────────────────────────
 *
 * Drafted rows are dimmed and struck through with "Drafted by X" in place of the action
 * cell, rather than being filtered out. The design asks for the same mechanism with slightly
 * different copy; the copy here follows the design ("Drafted 1.09 · Dress Bot 07"), the
 * mechanism follows what shipped. Do NOT reinstate a `.filter(p => !draftedIds.has(p.id))` --
 * that is what SAB-843 removed.
 */

export interface PlayerRow {
  id: string;
  full_name: string;
  position: string;
  nfl_team?: string | null;
  /**
   * SAB-813: the WEEKLY INJURY REPORT. Until this ticket the pool mapping wrote the roster
   * state into this field, so the name described the wrong column. It now means what it
   * says, and `status` below carries the roster state.
   */
  injury_status?: string | null;
  /** SAB-813: roster state (ACT / IR / PUP / NON / EXE / IRD / UFA ...). */
  status?: string | null;
  proj_sab_pts?: number | null;
  adp?: number | null;
  bye_week?: number | null;
  /**
   * SAB-931: the player's photo. This column rendered a grey placeholder circle and never a
   * face, which made Guess Who unplayable here: that card masks everything EXCEPT the
   * headshot, so without a photo a masked viewer sees a grid of identical grey circles and
   * the card they paid for communicates nothing. The pool query already returned this
   * field; only the row shape dropped it.
   */
  headshot_url?: string | null;
}

export interface PlayersTableProps {
  players: PlayerRow[];
  loading?: boolean;
  /** playerId -> "1.09 · Dress Bot 07". Presence means drafted. */
  draftedLabel: Map<string, string>;
  queuedIds: Set<string>;
  onToggleQueue: (playerId: string) => void;
  onDraft: (playerId: string) => void;
  /** Draft buttons are enabled ONLY on the user's turn. */
  isMyTurn: boolean;
  drafting?: boolean;
  /**
   * What to say when there are no rows, instead of an empty table body.
   *
   * The pool is not fetched once a draft is over -- deliberately, see NFLDraftRoomV2 -- so
   * "no rows" is the NORMAL state of a completed draft, not a failure. A blank body left
   * the toolbar reading "0 players" next to 190 picks, which states something false about
   * the pool rather than declining to state anything.
   */
  /**
   * SAB-959: `action` is an optional affordance rendered under the body — the README's
   * "Switch to ALL" for a filter that has emptied the list. A LABEL AND A CALLBACK rather
   * than a flag, so this component never has to know which control the caller uses; the
   * mobile room has an ALL/AVAILABLE segment and the desktop room a HIDE DRAFTED toggle,
   * and both can hand down the same "put the drafted players back" action.
   */
  emptyState?: { title: string; body: string; action?: { label: string; onClick: () => void } };
  /**
   * SAB-931: open the player detail modal.
   *
   * OMITTED ENTIRELY while a Guess Who mask is active -- the room does not pass it -- so a
   * masked name is not clickable at all. There is deliberately no `masked` prop here: a
   * second "am I masked" condition in this file could drift out of sync with the masking
   * itself, and the modal fetches the real player straight from nfl_players (name,
   * position, injury, game stats), so one click that got through would leak everything the
   * card hides. Structurally impossible beats conditionally prevented.
   */
  onPlayerClick?: (playerId: string) => void;
  /**
   * SAB-989: UPPERCASE position -> why this team may not draft another one, already phrased
   * for a human. Presence means blocked, exactly like `draftedLabel` above.
   *
   * Keyed by POSITION and not by player id, because that is what the rule is about: the cap
   * counts how many of a position a roster holds, so every QB row is blocked or none is. A
   * per-player map would be 1,872 entries recomputed on every pick to say one thing.
   *
   * This is an AFFORDANCE, never the control. guard_nfl_position_limit refuses the roster
   * insert whatever the client believes, and the auto-pick selectors skip capped positions
   * for themselves. If this prop is stale or absent the worst case is a Draft click that
   * comes back refused -- not a cap that quietly stops applying.
   */
  cappedPositions?: ReadonlyMap<string, string>;
}

/**
 * SAB-940: the name column has to survive a phone.
 *
 * MEASURED, not derived — arithmetic over these class names is what missed it originally.
 * Name cell width in the real browser: 390px 0 -> 141px, 430px 0 -> 181px.
 *
 * Every other column here is a FIXED width, so the 1fr name column gets whatever is left —
 * and below ~420px there is nothing left. The six-column form totals
 * 34+78+66+66+132 = 376px of fixed track, plus 12.5px of gaps and 32px of px-4 padding =
 * 420.5px, against a 390px viewport. The name collapsed to zero width and simply was not
 * painted: measured in the browser, a row's textContent read "1????———Draft" while its
 * innerText read "1 ? — — — Draft". The row was visible; the name inside it was not.
 *
 * Three things give the name room below `sm`, and the first alone was NOT enough:
 *
 *   1. ADP and Bye are dropped — the two least useful numbers while actually on the clock.
 *      This got the cell to 73px, and a 73px cell still rendered "New…" OVERLAPPING the
 *      Trash Proj column, because the pill and headshot inside it are ~86px on their own.
 *      Measuring only the track width would have called that fixed; the screenshot did not.
 *   2. The position pill is hidden below `sm`. It lives INSIDE the name cell and was eating
 *      most of it; the pool's position filter chips already carry that information.
 *   3. Rank, Trash Proj and the action column are tightened (34/78/132 -> 26/62/104), with
 *      gap-2 and px-3 instead of gap-2.5 and px-4.
 *
 * Trash Proj stays visible: it is the scoring signal this game is built on, and hiding it
 * would be a different kind of wrong.
 *
 * The column count differs between breakpoints, so all THREE users of this grid — header,
 * skeleton and row — hide the same two cells. A cell hidden in one and not the others
 * silently shifts that row by a column.
 */
const GRID = 'grid grid-cols-[26px_1fr_62px_104px] sm:grid-cols-[34px_1fr_78px_66px_66px_132px] gap-2 sm:gap-2.5 px-3 sm:px-4';

function PositionPill({ position }: { position: string }) {
  const c = getDraftBoardPosColor(position);
  return (
    <span
      className={cn(
        'inline-flex h-5 w-9 flex-none items-center justify-center rounded border',
        'font-display font-bold text-[9.5px]',
        c.text, c.border, c.bgFaint,
      )}
    >
      {position}
    </span>
  );
}

export default function PlayersTable({
  players, loading, draftedLabel, queuedIds, onToggleQueue, onDraft, isMyTurn, drafting,
  emptyState, onPlayerClick, cappedPositions,
}: PlayersTableProps) {
  /**
   * SAB-996 item 4: VIRTUALIZED.
   *
   * The pool is ~1,872 rows (v_draftable_players) and every one of them was mounted, each
   * with two buttons, a headshot and a pill. On a mid-range laptop that is the lag Adam
   * reported: not the fetch, which happens once, but the re-render that follows every pick,
   * search keystroke, filter change and mask toggle.
   *
   * @tanstack/react-virtual was already a dependency (react-query's sibling), so this adds
   * no package. `overscan` is deliberately generous -- the row height varies with the
   * injury pill and the team line, and a tight overscan shows blank bands while fast
   * scrolling, which during a draft reads as the pool having lost the player you were
   * hunting. `measureElement` corrects the estimate per row rather than trusting it.
   *
   * The scroll CONTAINER keeps data-testid="players-rows" and every row keeps
   * data-testid="player-row", because the e2e specs locate both. Those specs stay valid
   * under virtualization: guess-who-masking pins itself to one player through the search
   * box rather than scanning the whole list, and draft-scroll explicitly ignores anything
   * inside an internal scroller.
   */
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: players.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 52,
    overscan: 12,
  });
  if (loading) {
    // Skeleton rows on the same 9px/16px rhythm as real rows (prompt 10).
    return (
      <div data-testid="players-skeleton" className="flex-1 min-h-0 overflow-y-auto">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className={cn(GRID, 'items-center border-b border-border/50 py-[9px]')}>
            <Skeleton className="h-3 w-4" />
            <div className="flex items-center gap-2.5">
              <Skeleton className="h-5 w-9 rounded" />
              <Skeleton className="h-7 w-7 rounded-full" />
              <Skeleton className="h-3.5 w-32" />
            </div>
            <Skeleton className="h-3.5 w-10 justify-self-end" />
            <Skeleton className="hidden sm:block h-3 w-8 justify-self-end" />
            <Skeleton className="hidden sm:block h-3 w-6 justify-self-end" />
            <div />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div
        className={cn(
          GRID,
          'flex-none border-b border-border bg-background py-2',
          'font-display font-bold text-[10px] uppercase tracking-[0.08em] text-muted-foreground',
        )}
      >
        <span>#</span>
        <span>Player</span>
        <span className="text-right">Trash Proj</span>
        <span className="hidden sm:block text-right">ADP</span>
        <span className="hidden sm:block text-right">Bye</span>
        <span />
      </div>

      <div ref={scrollRef} data-testid="players-rows" className="flex-1 min-h-0 overflow-y-auto">
        {players.length === 0 && emptyState && (
          <div
            data-testid="players-empty"
            className="flex h-full flex-col items-center justify-center gap-1.5 px-8 py-14 text-center"
          >
            <p className="font-display text-[12.5px] font-bold tracking-[0.04em] text-foreground">
              {emptyState.title}
            </p>
            <p className="max-w-[38ch] text-[11.5px] leading-relaxed text-muted-foreground">
              {emptyState.body}
            </p>
            {emptyState.action && (
              <button
                data-testid="players-empty-action"
                onClick={emptyState.action.onClick}
                className={cn(
                  'mt-[6px] h-[36px] rounded-full border border-primary bg-primary/[0.16] px-[14px]',
                  'font-display text-[11px] font-bold tracking-[0.06em] text-primary',
                )}
              >
                {emptyState.action.label}
              </button>
            )}
          </div>
        )}
        <div style={{ height: players.length ? virtualizer.getTotalSize() : undefined, position: 'relative' }}>
        {virtualizer.getVirtualItems().map(v => {
          // The REAL index, not the position within the rendered window -- `isTop` and the
          // rank number both depend on it, and using the window index would label whatever
          // happened to be scrolled to the top as pick #1.
          const i = v.index;
          const p = players[i];
          const drafted = draftedLabel.get(p.id);
          const queued = queuedIds.has(p.id);
          const isTop = i === 0 && !drafted;
          // SAB-989: only for rows still in play. A drafted row already says why it cannot
          // be drafted, and stacking a cap reason on top of "Drafted 1.09" would replace the
          // more specific answer with the vaguer one.
          const capReason = drafted ? undefined : cappedPositions?.get((p.position ?? '').toUpperCase());

          return (
            <div
              key={p.id}
              ref={virtualizer.measureElement}
              data-index={i}
              data-testid="player-row"
              data-player-id={p.id}
              data-drafted={drafted ? 'true' : undefined}
              data-capped={capReason ? 'true' : undefined}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', transform: `translateY(${v.start}px)` }}
              className={cn(
                GRID, 'items-center border-b border-border/50 py-[9px]',
                isTop && 'border-l-[3px] border-l-primary bg-primary/[0.06]',
                drafted && 'opacity-[0.42]',
                // Dimmer than a live row, lighter than a drafted one: this player IS still
                // available, just not to this team right now.
                capReason && 'opacity-[0.6]',
              )}
            >
              <span className={cn('font-display font-bold text-xs', isTop ? 'text-primary' : 'text-muted-foreground')}>
                {i + 1}
              </span>

              <div className="flex items-center gap-2.5 min-w-0">
                <span className="hidden sm:contents"><PositionPill position={p.position} /></span>
                {p.headshot_url ? (
                  <img
                    data-testid="player-headshot"
                    src={p.headshot_url}
                    // The name may be masked; the alt text must not leak it back. Screen
                    // readers are a render surface like any other.
                    alt={p.full_name}
                    loading="lazy"
                    className="h-7 w-7 flex-none rounded-full bg-muted object-cover"
                  />
                ) : (
                  <span className="h-7 w-7 flex-none rounded-full bg-muted" aria-hidden />
                )}
                <span className="min-w-0">
                  {onPlayerClick ? (
                    <button
                      type="button"
                      data-testid="player-name-button"
                      onClick={() => onPlayerClick(p.id)}
                      className={cn(
                        'block max-w-full truncate text-left text-[13.5px] font-bold text-white',
                        'hover:text-primary transition-colors',
                        drafted && 'line-through',
                      )}
                    >
                      {p.full_name}
                    </button>
                  ) : (
                    <span className={cn('block truncate text-[13.5px] font-bold text-white', drafted && 'line-through')}>
                      {p.full_name}
                    </span>
                  )}
                  {/*
                    SAB-813: the sub-line used to read "LV · ACT" -- the roster state as
                    muted grey text, identical in weight to the team alias, on every one of
                    3,140 healthy rows. It was information nobody read, and the 176 players
                    who are NOT active looked exactly like the ones who are.
                    Now: ACT prints nothing, and any other designation becomes the pill.
                  */}
                  <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    {drafted ? (
                      <span className="truncate">{`Drafted ${drafted}`}</span>
                    ) : capReason ? (
                      <span className="truncate text-amber-400/90" data-testid="cap-reason">{capReason}</span>
                    ) : (
                      <>
                        {p.nfl_team && <span className="truncate">{p.nfl_team}</span>}
                        {/* flex-none inside a truncating row: the team alias gives up width
                            before the designation does. A clipped "PUP" is worse than a
                            clipped "LV". */}
                        <AvailabilityPill injuryStatus={p.injury_status} status={p.status} />
                      </>
                    )}
                  </span>
                </span>
              </div>

              <span className="text-right font-display text-sm font-extrabold tabular-nums text-damage">
                {p.proj_sab_pts != null ? p.proj_sab_pts.toFixed(1) : '—'}
              </span>
              <span className="hidden sm:block text-right font-mono text-xs text-muted-foreground">
                {p.adp != null ? p.adp.toFixed(1) : '—'}
              </span>
              <span className="hidden sm:block text-right font-mono text-xs text-muted-foreground">
                {p.bye_week ?? '—'}
              </span>

              <div className="flex items-center justify-end gap-1.5">
                {!drafted && (
                  <>
                    {/*
                      SAB-989: the queue stays OPEN on a capped row, deliberately. A cap is
                      not a property of the player, it is the state of this roster right now
                      -- drop a QB, or have the commissioner raise the limit, and the entry
                      becomes draftable. selectFromQueue skips a capped entry and scans on
                      rather than failing, so a queued-but-capped player costs nothing and
                      is there the moment it is legal again. Only the immediate action is
                      refused.
                    */}
                    <Button
                      data-testid="queue-toggle"
                      aria-label={queued ? 'Remove from queue' : 'Add to queue'}
                      aria-pressed={queued}
                      variant="outline"
                      size="icon"
                      onClick={() => onToggleQueue(p.id)}
                      className={cn(
                        'h-[30px] w-[30px] rounded-lg',
                        queued && 'border-success/40 bg-success/[0.12] text-success',
                      )}
                    >
                      {queued ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                    </Button>
                    <Button
                      data-testid="draft-button"
                      disabled={!isMyTurn || drafting || !!capReason}
                      title={capReason}
                      aria-label={capReason ? `Draft ${p.full_name} — ${capReason}` : undefined}
                      onClick={() => onDraft(p.id)}
                      className={cn(
                        'h-[30px] rounded-lg px-4 font-display text-xs font-extrabold',
                        isMyTurn && !capReason
                          ? 'bg-[image:var(--gradient-sabotage)] text-[#12130f] hover:opacity-90'
                          : 'bg-muted text-[#c9cdd6] border border-border hover:bg-muted',
                      )}
                    >
                      Draft
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}
